import React, {memo, useRef, useEffect, useState} from 'react';
import styled from 'styled-components';
import {
   ControlButton,
   TscButtonType,
   Spinner,
   TscThemeName,
   TscThemeNames,
   TscStyles,
   TextInput
} from '@techsmith/tsc-cloud-style-guide';
import {
   IMemberDetailsWithProfileImageModel,
   IUserProfileWithEmailModel
} from '../../model/appModel';
import Constants, {GroupRoleTypes} from '../../constants/Constants';
import {useTscTheme} from '../../context/themeProvider';
import {useTranslation} from 'react-i18next';
import cssConstants from '../../constants/cssConstants';
import {StyledWarning, TeamsModalLabel} from '../util/StyledElements';
import {scrollElementIntoView} from '../../util/CommentScroller';
import profileApi from '../../service/profileApi';
import TeamMemberListItem from '../modals/ShareTeamModal/TeamMemberListItem';

const MemberInviteContainerWrapper = styled.div`
   text-align:left;
`;

const MembersCount = styled.span<{theme: TscThemeNames}>`
   background-color: ${props =>
      props.theme === TscThemeName.dusk
         ? TscStyles.color.ui.dusk.dark
         : TscStyles.color.ui.dawn.lightMedium};
   margin-left: 0.75rem;
   padding: 0.25rem;
   border-radius: ${TscStyles.border.radius.sm};
   font-size: 0.875rem;
   font-weight: ${TscStyles.font.weight.semibold};
   cursor: pointer;
`;

const EmailInputContainer = styled.div`
   display: flex;
   margin-bottom: 1rem;

   > button {
      margin-left: 0.5rem;
   }
`;

const FlexContainer = styled.div`
   display: flex;
   flex-direction: column;
   width: 100%;
`;

const EmailListContainer = styled.div<{theme: TscThemeNames}>`
   position: relative;
   display: flex;
   flex-direction: column;
   min-height: 44px;
   max-height: 10rem;
   border: 1px solid
      ${props =>
         props.theme === TscThemeName.dusk
            ? TscStyles.color.ui.dusk.dark
            : TscStyles.color.ui.dawn.medium};
   border-radius: ${TscStyles.border.radius.md};
   overflow-x: hidden;
   overflow-y: scoll;
`;

const LoadingContainer = styled.div`
   margin: 0.25rem 0;
   height: 2.25rem;
   min-height: 2.25rem;
   width: 100%;
   display: flex;
   overflow: hidden;
   height: 100%;
   align-items: center;
`;

const EmptyState = styled.span`
   display: flex;
   justify-content: center;
   align-items: center;
   height: 100%;
   font-size: 1rem;
   font-weight: ${TscStyles.font.weight.semibold};
`;

const MemberInputSubMessage = styled.div<{theme: TscThemeNames}>`
   margin-top: 0.25rem;
   font-size: 13px;
   color: ${props => props.theme === TscThemeName.dawn ? TscStyles.color.text.mediumDark : TscStyles.color.text.light};
`;

const emailRegExp = new RegExp(Constants.emailValidationRegexp);

export interface IMemberInviteContainerState {
   emailInput: string;
   isValidEmailInput: boolean;
   inputValidationErrorMessage: string;
}

export const MemberInviteContainerBase: React.FC<IMemberInviteContainerProps> = ({
   labelText,
   members,
   isWorking,
   disableFormFields,
   memberInputSubMessage,
   maxMemberTooltip,
   membersCount,
   membersLimit,
   hideMemberLimit,
   memberPendingInviteText,
   removeMember,
   addMember,
   updateMember
}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();

   const [state, setState] = useState<IMemberInviteContainerState>({
      emailInput: '',
      isValidEmailInput: true,
      inputValidationErrorMessage: ''
   });

   const updateState = (updatedValues: Partial<IMemberInviteContainerState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const loadSpinnerRef = useRef(null);
   const emailInputRef = useRef(state.emailInput);
   const showEmptyState = !members.length && !isWorking;
   const memberContainerId = 'email-list-container';
   const onKeyUpTimeoutInMs = 500;
   const timeoutRef = useRef(null);
   const errorMessageInvalidEmailInputFormat = t('general.invalidEmailErrorCommaDelimited');

   useEffect(() => {
      isWorking &&
         scrollElementIntoView(loadSpinnerRef.current, memberContainerId);
   }, [isWorking]);

   useEffect(() => {
      emailInputRef.current = state.emailInput;
   });

   const validateEmailAddress = (inputEmail: string): boolean => emailRegExp.test(inputEmail);

   const onMembersInputChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      if (timeoutRef.current) {
         clearTimeout(timeoutRef.current);
      }

      updateState({isValidEmailInput: true, inputValidationErrorMessage: '', emailInput: evt.target.value});

      timeoutRef.current = setTimeout(() => {
         timeoutRef.current = null;
         validateEmailInput(emailInputRef.current);
      }, onKeyUpTimeoutInMs);
   };

   const validateEmailInput = (emailInputString: string) => {
      const email = emailInputString.trim();
      if (email.length === 0) {
         updateState({isValidEmailInput: true, inputValidationErrorMessage: ''});
         return;
      }

      const emails = email.split(',');
      emails.filter(e => e.length > 0).forEach(e => {
         const validEmail = validateEmailAddress(e.trim());
         if (!validEmail) {
            updateState({isValidEmailInput: false, inputValidationErrorMessage: errorMessageInvalidEmailInputFormat});
         } else {
            updateState({isValidEmailInput: true, inputValidationErrorMessage: ''});
         }
      });
   };

   const addEmailsToList = async () => {
      const emails = Array.from(new Set(state.emailInput.split(',').map(e => e.trim()).filter(e => e.length > 0)));
      const remainingSpots = membersLimit - membersCount;

      if (emails.length > remainingSpots) {
         const errorMessage = t('team.modal.create.errorAddingMembersOverLimit', {remainingMemberCount: remainingSpots});
         updateState({isValidEmailInput: false, inputValidationErrorMessage: errorMessage});
         return;
      }

      const newEmails = emails.filter(e => !members.some(m => m.EmailAddress === e));

      const batchSize = 3;
      const batches = [];

      for (let i = 0; i < newEmails.length; i += batchSize) {
         batches.push(newEmails.slice(i, i + batchSize));
      }

      for (const batch of batches) {
         await Promise.all(batch.map(async emailAddress => {
            const addedMember: IUserProfileWithEmailModel = {
               email: emailAddress,
               displayName: emailAddress,
               profileImageUrl: null,
               techSmithId: null
            };

            await addMember(addedMember);

            if (updateMember) {
               try {
                  const partialUserProfile = await profileApi.getUserProfileInfoFromEmail(emailAddress);
                  const updatedMember = {
                     ...partialUserProfile,
                     email: emailAddress
                  };

                  updateMember(updatedMember);
               } catch (e) { /* empty */}
            }
         }));
      }

      updateState({emailInput: ''});
   };

   const onEnterPressed = async (): Promise<void> => {
      if (state.isValidEmailInput) {
         await addEmailsToList();
      }
   };

   return (
      <MemberInviteContainerWrapper>
         <div title={t(maxMemberTooltip, {membersLimit})}>
            <TeamsModalLabel>
               {labelText}
            </TeamsModalLabel>
            {!hideMemberLimit && (
               <MembersCount
                  data-test-id="members-count"
                  theme={theme}
               >
                  {membersCount}
                  /
                  {membersLimit}
               </MembersCount>
            )}
         </div>
         <EmailInputContainer>
            <FlexContainer>
               <TextInput
                  disabled={disableFormFields}
                  value={state.emailInput}
                  onChange={onMembersInputChange}
                  themeName={theme}
                  onEnter={onEnterPressed}
                  onBlur={() => validateEmailInput(state.emailInput)}
                  sharedId="invite-container-email-input"
                  placeholder={t('team.modal.create.membersTextBoxPlaceholder')}
                  required={false}
                  tabIndex={0}
                  testId="member-invite-container-add-email-field"
               />
               {!state.isValidEmailInput && state.inputValidationErrorMessage.length > 0 && (
                  <StyledWarning id="error-message">
                     {state.inputValidationErrorMessage}
                  </StyledWarning>
               )}
               {memberInputSubMessage && state.isValidEmailInput && (
                  <MemberInputSubMessage theme={theme}>
                     {memberInputSubMessage}
                  </MemberInputSubMessage>
               )}
            </FlexContainer>
            <ControlButton
               buttonType={TscButtonType.secondary}
               label={t('general.add')}
               onClick={addEmailsToList}
               themeName={theme}
               width={cssConstants.smallButtonWidth}
               disabled={disableFormFields || state.emailInput.length === 0 || !state.isValidEmailInput || isWorking || membersCount >= membersLimit}
               testId="member-invite-container-add-email-button"
            />
         </EmailInputContainer>
         <EmailListContainer
            theme={theme}
            id={memberContainerId}
         >
            {showEmptyState && (
               <EmptyState>
                  {t('general.noMembers')}
               </EmptyState>
            )}
            {members.map((m: IMemberDetailsWithProfileImageModel) => (
               <TeamMemberListItem
                  member={m}
                  memberPendingInviteText={memberPendingInviteText}
                  onRemoveClick={
                     removeMember && m.Role !== GroupRoleTypes.manager
                        ? async () => await removeMember(m)
                        : null
                  }
                  key={m.EmailAddress}
               />
            ))}
            {isWorking && (
               <LoadingContainer ref={loadSpinnerRef}>
                  <Spinner
                     testId="loading-spinner"
                     themeName={theme}
                  />
               </LoadingContainer>
            )}
         </EmailListContainer>
      </MemberInviteContainerWrapper>
   );
};

export interface IMemberInviteContainerProps {
   labelText: string;
   members: IMemberDetailsWithProfileImageModel[];
   disableFormFields?: boolean;
   isWorking: boolean;
   membersCount: number;
   membersLimit: number;
   memberInputSubMessage: string;
   maxMemberTooltip: string;
   hideMemberLimit?: boolean;
   memberPendingInviteText: string;
   removeMember?(member: IMemberDetailsWithProfileImageModel): Promise<void>;
   addMember?(addedMember: IUserProfileWithEmailModel): Promise<void>;
   updateMember?(updatedMember: IUserProfileWithEmailModel): void;
}

export default memo(MemberInviteContainerBase);